import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Checkbox,
  Tooltip,
  Fade,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  Archive as ArchiveIcon,
  SelectAll as SelectAllIcon,
  DeselectOutlined as DeselectIcon,
  Check,
  Flag as ResolveIcon,
  MoreVert as ActionsIcon,
} from "@material-ui/icons";
import { useBulkSelection } from "../../context/BulkSelection/BulkSelectionContext";
import CloseTicketModal from "../CloseTicketModal";
import AcceptTicketModal from "../AcceptTicketWithoutQueueModal";
import ResolveTicketModal from "../ResolveTicketModal";
import BulkActionsPopup from "../BulkActionsPopup";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    transition: theme.transitions.create(["transform"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    minHeight: 48,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  counter: {
    flexGrow: 1,
    marginLeft: theme.spacing(2),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  actionButton: {
    color: theme.palette.primary.contrastText,
    border: `1px solid ${theme.palette.primary.contrastText}`,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
}));

const BulkActionHeader = ({ availableTickets = [], status }) => {
  const classes = useStyles();
  const {
    isSelectionMode,
    selectedTickets,
    selectAll,
    disableSelectionMode,
    selectAllTickets,
    deselectAllTickets,
  } = useBulkSelection();

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);

  const handleClose = () => {
    disableSelectionMode();
  };

  const handleSelectAll = () => {
    if (selectAll || selectedTickets.length === availableTickets.length) {
      deselectAllTickets();
    } else {
      selectAllTickets(availableTickets);
    }
  };

  const handleArchiveAction = () => {
    setBulkModalOpen(true);
  };

  const handleBulkModalClose = () => {
    setBulkModalOpen(false);
  };

  const handleAcceptAction = () => {
    setAcceptModalOpen(true);
  };

  const handleAcceptModalClose = () => {
    setAcceptModalOpen(false);
  };

  const handleResolveAction = () => {
    setResolveModalOpen(true);
  };

  const handleResolveModalClose = () => {
    setResolveModalOpen(false);
  };

  const handleBulkActionSuccess = () => {
    disableSelectionMode();
    // Não precisa mais recarregar a página - o toast e a atualização em tempo real já cuidam disso
  };

  const handleActionsClick = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const allSelected = selectAll || selectedTickets.length === availableTickets.length;
  const someSelected = selectedTickets.length > 0 && selectedTickets.length < availableTickets.length;

  // Verificar se há ações disponíveis para o status atual
  const hasAvailableActions = () => {
    if (status === "pending") return true;
    if (status === "open") return true;
    if (status === "closed" || status === "archived") return false;
    return false;
  };

  if (!isSelectionMode) {
    return null;
  }

  return (
    <>
      <Fade in={isSelectionMode}>
        <AppBar position="relative" className={classes.appBar} elevation={2}>
          <Toolbar className={classes.toolbar}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="fechar seleção"
            >
              <CloseIcon />
            </IconButton>

            <Typography variant="h6" className={classes.counter}>
              {selectedTickets.length} selecionado{selectedTickets.length !== 1 ? "s" : ""}
            </Typography>

            <Box className={classes.actionButtons} style={{ justifyContent: 'center', alignItems: 'center', width: '100%', display: 'flex' }}>
              <Tooltip title="Selecionar todos">
                <IconButton color="inherit" onClick={handleSelectAll} style={{ padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    style={{ color: "inherit", padding: 0 }}
                  />
                </IconButton>
              </Tooltip>
              
              {hasAvailableActions() && (
                <Tooltip title="Ações">
                  <IconButton
                    color="inherit"
                    onClick={handleActionsClick}
                    disabled={selectedTickets.length === 0}
                    style={{ marginLeft: 8, marginRight: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ActionsIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </Fade>

      <BulkActionsPopup
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={handleActionsClose}
        status={status}
        onAccept={handleAcceptAction}
        onResolve={handleResolveAction}
        onArchive={handleArchiveAction}
        acceptLoading={acceptLoading}
        resolveLoading={resolveLoading}
        archiveLoading={archiveLoading}
        disabled={selectedTickets.length === 0}
      />

      <CloseTicketModal
        modalOpen={bulkModalOpen}
        onClose={(confirmed) => {
          handleBulkModalClose();
          if (confirmed) {
            handleBulkActionSuccess();
          }
        }}
        tickets={selectedTickets}
        action="archive"
      />

      <AcceptTicketModal
        modalOpen={acceptModalOpen}
        onClose={(confirmed) => {
          handleAcceptModalClose();
          if (confirmed) {
            handleBulkActionSuccess();
          }
          setAcceptLoading(false);
        }}
        tickets={selectedTickets}
        showTagPreview
        setLoading={setAcceptLoading}
      />

      <ResolveTicketModal
        modalOpen={resolveModalOpen}
        onClose={(confirmed) => {
          handleResolveModalClose();
          if (confirmed) {
            handleBulkActionSuccess();
          }
          setResolveLoading(false);
        }}
        tickets={selectedTickets}
        setLoading={setResolveLoading}
      />
    </>
  );
};

export default BulkActionHeader; 